import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'

function getPackageDisplayName(item) {
  if (!item) return ''
  return item.label ? `${item.label} (${item.package_name})` : item.package_name
}

function getDefaultOptionValue(item) {
  if (typeof item === 'string') {
    return item
  }
  if (!item || typeof item !== 'object') {
    return ''
  }
  return item.package_name || item.value || item.label || item.id || ''
}

function getDefaultOptionDisplayName(item) {
  if (typeof item === 'string') {
    return item
  }
  if (!item || typeof item !== 'object') {
    return ''
  }
  return getPackageDisplayName(item) || item.label || item.value || item.id || ''
}

function getDefaultSearchTexts(item) {
  if (typeof item === 'string') {
    return [item]
  }
  if (!item || typeof item !== 'object') {
    return []
  }
  return [
    item.package_name,
    item.label,
    item.value,
    item.id,
    getDefaultOptionDisplayName(item)
  ].filter(Boolean)
}

export default function PackageSearchSelect({
  value,
  onChange,
  options = [],
  loading = false,
  multiple = false,
  placeholder = '全部安装包',
  className = '',
  getOptionValue,
  getOptionDisplayName,
  getSearchTexts,
  searchPlaceholder = '搜索安装包名/标签'
}) {
  const containerRef = useRef(null)
  const searchInputRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState('')

  const resolveOptionValue = (item) => {
    if (!item) return ''
    return typeof getOptionValue === 'function' ? getOptionValue(item) : getDefaultOptionValue(item)
  }

  const resolveOptionDisplayName = (item) => {
    if (!item) return ''
    return typeof getOptionDisplayName === 'function' ? getOptionDisplayName(item) : getDefaultOptionDisplayName(item)
  }

  const resolveSearchTexts = (item) => {
    if (!item) return []
    return typeof getSearchTexts === 'function' ? getSearchTexts(item) : getDefaultSearchTexts(item)
  }

  const normalizeComparableValue = (item) => String(item ?? '')

  const selectedValues = useMemo(() => {
    if (!multiple) {
      return []
    }
    return Array.isArray(value) ? value.filter((item) => item != null && item !== '') : []
  }, [multiple, value])

  const selectedValueSet = useMemo(
    () => new Set(selectedValues.map((item) => normalizeComparableValue(item))),
    [selectedValues]
  )

  const selectedItem = useMemo(
    () => options.find((item) => resolveOptionValue(item) === value) || null,
    [getOptionValue, options, value]
  )

  const selectedItems = useMemo(() => {
    if (!multiple) {
      return []
    }
    return options.filter((item) => selectedValueSet.has(normalizeComparableValue(resolveOptionValue(item))))
  }, [multiple, options, selectedValueSet])

  const filteredOptions = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase()
    if (!normalizedKeyword) {
      return options
    }

    return options.filter((item) => {
      return resolveSearchTexts(item).some((text) =>
        String(text || '').toLowerCase().includes(normalizedKeyword)
      )
    })
  }, [keyword, options, getOptionDisplayName, getSearchTexts])

  useEffect(() => {
    if (!open) {
      setKeyword('')
      return
    }

    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)

    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [])

  const handleSelect = (nextValue) => {
    if (multiple) {
      const normalizedNextValue = normalizeComparableValue(nextValue)
      const nextValues = selectedValues.filter((item) => normalizeComparableValue(item) !== normalizedNextValue)

      if (!selectedValueSet.has(normalizedNextValue)) {
        nextValues.push(nextValue)
      }

      onChange?.(nextValues)
      return
    }

    onChange?.(nextValue)
    setOpen(false)
  }

  const handleClearAll = () => {
    onChange?.(multiple ? [] : '')
    if (!multiple) {
      setOpen(false)
    }
  }

  const buttonText = useMemo(() => {
    if (multiple) {
      if (selectedItems.length === 0) return placeholder
      if (selectedItems.length === 1) return resolveOptionDisplayName(selectedItems[0])
      return `已选 ${selectedItems.length} 项`
    }

    return selectedItem ? resolveOptionDisplayName(selectedItem) : placeholder
  }, [multiple, placeholder, resolveOptionDisplayName, selectedItem, selectedItems])

  return (
    <div ref={containerRef} className={`relative min-w-[260px] ${className}`.trim()}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={loading}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
      >
        <span className={`truncate text-left ${(multiple ? selectedItems.length > 0 : selectedItem) ? 'text-gray-900' : 'text-gray-500'}`}>
          {buttonText}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className="absolute left-0 top-full z-20 mt-2 w-full rounded-xl border border-gray-200 bg-white p-2 shadow-lg">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-9 text-sm outline-none transition focus:border-blue-400"
            />
            {keyword ? (
              <button
                type="button"
                onClick={() => setKeyword('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="mt-2 max-h-72 overflow-y-auto">
            <button
              type="button"
              onClick={handleClearAll}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className="truncate">{multiple ? '清空已选' : placeholder}</span>
              {multiple ? (
                selectedItems.length === 0 ? <Check className="h-4 w-4 text-blue-600" /> : null
              ) : (
                !value ? <Check className="h-4 w-4 text-blue-600" /> : null
              )}
            </button>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((item) => {
                const optionValue = resolveOptionValue(item)
                const optionDisplayName = resolveOptionDisplayName(item)
                const isSelected = multiple
                  ? selectedValueSet.has(normalizeComparableValue(optionValue))
                  : optionValue === value
                return (
                  <button
                    key={String(optionValue || optionDisplayName)}
                    type="button"
                    onClick={() => handleSelect(optionValue)}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                    title={optionDisplayName}
                  >
                    <span className="truncate">{optionDisplayName}</span>
                    {isSelected ? <Check className="h-4 w-4 shrink-0 text-blue-600" /> : null}
                  </button>
                )
              })
            ) : (
              <div className="px-3 py-6 text-center text-sm text-gray-500">未找到匹配安装包</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
